import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, Inject, input, OnInit, output, signal } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatFormField, MatHint, MatLabel, MatPrefix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatSlideToggle, MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatSlider, MatSliderThumb } from '@angular/material/slider';
import { MatTab, MatTabContent, MatTabGroup } from '@angular/material/tabs';
import {
  AppType,
  Charity,
  ENVIRONMENT,
  Environment,
  SplitScreen,
  SplitScreenSection,
  SplitScreenSectionsTypes,
  SplitScreenVersion,
} from '@domains';
import {
  CardComponent,
  FxFlexDirective,
  FxLayoutDirective,
  FxLayoutGapDirective,
  PhotoUploadComponent,
  Responsive,
  ResponsiveService,
} from '@rspl-ui';
import { ColorPickerModule } from 'ngx-color-picker';

type fieldPair = [string, (value: boolean | SplitScreenVersion) => void];

@Component({
  selector: 'app-split-screen-form',
  templateUrl: './split-screen-form.component.html',
  styleUrls: ['./split-screen-form.component.scss'],
  imports: [
    FxLayoutDirective,
    FxFlexDirective,
    MatIcon,
    FxLayoutGapDirective,
    FormsModule,
    ReactiveFormsModule,
    CardComponent,
    MatSlideToggle,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    PhotoUploadComponent,
    MatInput,
    MatHint,
    MatTabGroup,
    MatTab,
    MatButtonToggleGroup,
    MatButtonToggle,
    MatPrefix,
    ColorPickerModule,
    MatSlider,
    MatSliderThumb,
    MatTabContent,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    CommonModule,
  ],
})
export class SplitScreenFormComponent extends Responsive implements OnInit {
  config = input<SplitScreen>(null);
  charity = input<Charity>(null);

  formChange = output<SplitScreen>();

  enabled = signal(false);

  form: FormGroup;
  isCaptain: boolean;
  splitScreenVersions = SplitScreenVersion;
  splitscreensectionTypes = SplitScreenSectionsTypes;
  versions = [
    { value: SplitScreenVersion.V1, text: 'Design v1' },
    { value: SplitScreenVersion.V2, text: 'Design v2' },
    { value: SplitScreenVersion.V3A, text: 'Design v3A' },
    { value: SplitScreenVersion.V3B, text: 'Design v3B' },
    { value: SplitScreenVersion.V4, text: 'Design v4' },
  ];

  constructor(
    override responsiveService: ResponsiveService,
    @Inject(ENVIRONMENT) environment: Environment,
  ) {
    super(responsiveService);
    this.isCaptain = environment.app === AppType.CAPTAIN;

    const formBuilder = new FormBuilder();

    this.form = formBuilder.group({
      enabled: [false],
      version: [SplitScreenVersion.V2],
      title: [''],
      text: [''],
      button: ['', [Validators.required]],
      url: ['', [Validators.required]],
      backgroundColor: ['', [Validators.required]],
      textColor: ['', [Validators.required]],
      btnBackgroundColor: ['', [Validators.required]],
      btnTextColor: ['', [Validators.required]],
      titleBackgroundColor: ['', [Validators.required]],
      titleTextColor: ['', [Validators.required]],
      logoBackgroundColor: ['', [Validators.required]],
      stripeBackgroundColor: ['', [Validators.required]],
      bulletColor: ['', [Validators.required]],
      primaryReady: [false],

      standardEnabled: [false],
      showRestrictedItems: [false],
      restrictedItemsUrl: ['', [Validators.required]],
      restrictedItemsText: ['', [Validators.required]],
      titlePrimary: [''],
      textPrimary: [''],
      buttonPrimary: [''],
      backgroundColorPrimary: ['', [Validators.required]],
      textColorPrimary: ['', [Validators.required]],
      btnBackgroundColorPrimary: ['', [Validators.required]],
      btnTextColorPrimary: ['', [Validators.required]],
      titleBackgroundColorPrimary: ['', [Validators.required]],
      titleTextColorPrimary: ['', [Validators.required]],
      logoBackgroundColorPrimary: ['', [Validators.required]],
      bulletColorPrimary: ['', [Validators.required]],
      standardReady: [false],

      reverse: [false],
      discounts: [false],
      uber: [false],
      sections: <SplitScreenSection[]>[],

      logo: [''],
      logoOffsetTop: [0],
      logoOffsetLeft: [0],
      logoSize: [0],
      logoOffsetTopMobile: [0],
      logoOffsetLeftMobile: [0],
      logoSizeMobile: [0],
      logoStandardOffsetTop: [0],
      logoStandardOffsetLeft: [0],
      logoStandardSize: [0],
      logoStandardOffsetTopMobile: [0],
      logoStandardOffsetLeftMobile: [0],
      logoStandardSizeMobile: [0],
    });
  }

  get isV1orV2() {
    return [SplitScreenVersion.V1, SplitScreenVersion.V2].includes(this.form.get('version').value);
  }

  get isV1() {
    return SplitScreenVersion.V1 === this.form.get('version').value;
  }

  override ngOnInit(): void {
    this.enabled.set(this.config()?.enabled);
    this.form.patchValue({ ...this.config() });

    this.form.get('enabled').value ? this.form.enable() : this.form.disable();

    this.subscribeToChange([
      ['standardEnabled', this.onStandardEnabledChange.bind(this)],
      ['showRestrictedItems', this.onShowRestrictedItemsChange.bind(this)],
      ['primaryReady', this.onPrimaryReadyChange.bind(this)],
      ['standardReady', this.onStandardReadyChange.bind(this)],
      ['version', this.onVersionChange.bind(this)],
    ]);

    this.onVersionChange(this.form.get('version').value);

    this.form.valueChanges.subscribe((value) => {
      this.formChange.emit({ ...this.form.getRawValue(), ...value });
    });
  }

  private subscribeToChange(fields: fieldPair[]) {
    fields.forEach(([field, onFieldChange]) => {
      this.form.get(field).valueChanges.subscribe((value) => onFieldChange(value));
    });
  }

  private setControlState(controlNames: string[], enabled: boolean): void {
    controlNames.forEach((name) => {
      const control = this.form.get(name);
      if (control) {
        enabled ? control.enable() : control.disable();
      }
    });
  }

  private onStandardEnabledChange(enabled: boolean): void {
    this.setControlState(
      [
        'url',
        'button',
        'title',
        'text',
        'logo',
        'backgroundColor',
        'textColor',
        'btnBackgroundColor',
        'btnTextColor',
        'titleBackgroundColor',
        'titleTextColor',
        'logoBackgroundColor',
        'bulletColor',
        'showRestrictedItems',
        'standardReady',
      ],
      enabled && this.form.get('enabled').value,
    );
  }

  private onPrimaryReadyChange(primaryReady: boolean): void {
    this.setControlState(['buttonPrimary'], primaryReady && this.isV1orV2 && this.form.get('enabled').value);
  }

  private onShowRestrictedItemsChange(showRestricted: boolean): void {
    const showRestrictedItems = showRestricted && this.form.get('standardEnabled').value && this.form.get('enabled').value;

    this.setControlState(['restrictedItemsUrl'], showRestrictedItems && this.isV1orV2);
    this.setControlState(['restrictedItemsText'], showRestrictedItems && this.isV1);
  }

  private onStandardReadyChange(standardReady: boolean): void {
    const isReady = (standardReady || !this.isV1orV2) && this.form.get('enabled').value && this.form.get('standardEnabled').value;
    this.setControlState(['url', 'button'], isReady);
  }

  private onEnabledChange(): void {
    this.onShowRestrictedItemsChange(this.form.get('showRestrictedItems').value);
    this.onPrimaryReadyChange(this.form.get('primaryReady').value);
    this.onStandardReadyChange(this.form.get('standardReady').value);
    this.onStandardEnabledChange(this.form.get('standardEnabled').value);
  }

  private onVersionChange(value: SplitScreenVersion): void {
    const textValidators = [SplitScreenVersion.V3A, SplitScreenVersion.V3B, SplitScreenVersion.V4].includes(value)
      ? [Validators.required]
      : [];

    this.form.get('text').setValidators(textValidators);
    this.form.get('text').updateValueAndValidity();

    this.onStandardEnabledChange(this.form.get('standardEnabled').value);
    this.onStandardReadyChange(this.form.get('standardReady').value);
    this.onPrimaryReadyChange(this.form.get('primaryReady').value);
    this.onShowRestrictedItemsChange(this.form.get('showRestrictedItems').value);
  }

  setBodyOverflow(value: 'hidden' | 'initial') {
    document.body.style.overflow = value;
  }

  getUploadedFileUrl(event: any): void {
    this.form.patchValue({
      logo: event,
      logoOffsetTop: 0,
      logoOffsetLeft: 0,
      logoSize: 0,
      logoOffsetTopMobile: 0,
      logoOffsetLeftMobile: 0,
      logoSizeMobile: 0,
      logoStandardOffsetTop: 0,
      logoStandardOffsetLeft: 0,
      logoStandardSize: 0,
      logoStandardOffsetTopMobile: 0,
      logoStandardOffsetLeftMobile: 0,
      logoStandardSizeMobile: 0,
    });
  }

  dropSection(event: CdkDragDrop<SplitScreenSection[]>) {
    const sections = this.form.get('sections').value;
    moveItemInArray(sections, event.previousIndex, event.currentIndex);
    this.form.patchValue({ sections });
  }

  setDefaultValue(event: Event, field: string, constant: string): void {
    const target = event.target as HTMLInputElement;
    if (target.value?.trim()?.length === 0) {
      this.form.get(field).setValue(SplitScreen.DEFAULT[this.form.get('version').value][constant]);
    }
  }

  onSectionToggle(isVisible: boolean, type: string): void {
    const sections = this.form.get('sections').value;
    const section = sections.find((section: SplitScreenSection) => section.type === type);
    section.isVisible = isVisible;
    this.form.patchValue({ sections });
  }

  onFormEnableToggle(event: MatSlideToggleChange) {
    this.form.get('enabled').setValue(event.checked);
    this.enabled.set(event.checked);

    event.checked ? this.form.enable() : this.form.disable();
    event.checked && this.onEnabledChange();
  }

  validateForm(): boolean {
    this.form.updateValueAndValidity();
    this.form.markAllAsTouched();
    if (!this.form.invalid) {
      return true;
    } else {
      return false;
    }
  }
}
